import { useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Field } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { useSetSiteDayNote } from '../../lib/queries';
import { useFormErrors } from '../../lib/useFormErrors';
import { formatDate, formatWeekday } from '../../lib/dates';
import type { SiteTile } from './planModel';

interface SiteDayInfoDialogProps {
  tile: SiteTile;
  date: string;
  onClose: () => void;
}

/**
 * Die Baustelle für genau einen Tag bearbeiten: ein Freitext ("Beton kommt
 * 7 Uhr"), der nur an diesem Tag an der Kachel steht. Die dauerhafte
 * Baustellen-Notiz im Reiter Baustellen bleibt davon unberührt.
 */
export function SiteDayInfoDialog({ tile, date, onClose }: SiteDayInfoDialogProps) {
  const toast = useToast();
  const { errors, capture, clear, fieldError } = useFormErrors();
  const [note, setNote] = useState(tile.note);
  const setDayNote = useSetSiteDayNote();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    clear();
    setDayNote.mutate(
      { siteId: tile.site.id, date, note: note.trim() },
      {
        onSuccess: (result) => {
          toast.success(result.note ? 'Info gespeichert.' : 'Info entfernt.');
          onClose();
        },
        onError: capture,
      },
    );
  };

  const busy = setDayNote.isPending;

  return (
    <Modal
      title="Baustelle bearbeiten"
      subtitle={`${tile.site.name} — nur ${formatWeekday(date)}, ${formatDate(date)}`}
      onClose={onClose}
      width="voll"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="submit" form="site-day-info-form" className="btn btn-signal" disabled={busy}>
            {busy ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </>
      }
    >
      <form id="site-day-info-form" className="stack" onSubmit={submit} noValidate>
        {errors.general ? (
          <p className="form-error" role="alert">
            {errors.general}
          </p>
        ) : null}

        <div className="form-grid">
          <span className="label form-section">Tages-Info</span>

          <Field
            label="Info für diesen Tag"
            error={fieldError('note')}
            hint="Steht nur an diesem Tag an der Kachel. Leer lassen und speichern entfernt die Info."
            className="span-alle"
          >
            {(props) => (
              <textarea
                {...props}
                className="input"
                rows={10}
                maxLength={500}
                autoFocus
                placeholder="z. B. Beton kommt 7 Uhr"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>
        </div>
      </form>
    </Modal>
  );
}
